import React, { useEffect, useRef, useState } from 'react';
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';
import './TransactionGraph.css';

function TransactionGraph({ data, onNodeClick }) {
  const containerRef = useRef(null);
  const networkRef = useRef(null);
  const [containerReady, setContainerReady] = useState(false);

  const getNodeColor = (node) => {
    // Tainted addresses get red/orange color with special styling
    if (node.is_tainted) {
      if (node.taint_verified) {
        // Verified tainted = solid red
        return {
          background: '#dc3545',
          border: '#a71d2a',
          borderWidth: 3,
          highlight: {
            background: '#e74c3c',
            border: '#c0392b'
          },
          font: {
            color: '#ffffff'
          }
        };
      } else {
        // Auto-detected tainted = orange with dashed border
        return {
          background: '#fd7e14',
          border: '#dc6502',
          borderWidth: 3,
          borderDashes: [5, 5],
          highlight: {
            background: '#ff922b',
            border: '#f76707'
          },
          font: {
            color: '#ffffff'
          }
        };
      }
    }

    // Known entities get blue color
    if (node.is_known_entity) {
      return {
        background: '#0d6efd',
        border: '#0a58ca',
        borderWidth: 2,
        highlight: {
          background: '#0b5ed7',
          border: '#084298'
        },
        font: {
          color: '#ffffff'
        }
      };
    }
    
    // Coinbase transactions get a special gold color
    if (node.is_coinbase) {
      return {
        background: '#FFD700',
        border: '#FFA500',
        highlight: {
          background: '#FFED4E',
          border: '#FF8C00'
        }
      };
    }
    
    // Color by path index for multi-path traces
    if (node.path_idx !== undefined) {
      const pathColors = [
        { background: '#4CAF50', border: '#388E3C' },  // Green - Path 0 (main)
        { background: '#2196F3', border: '#1976D2' },  // Blue - Path 1
        { background: '#9C27B0', border: '#7B1FA2' },  // Purple - Path 2
        { background: '#FF9800', border: '#F57C00' },  // Orange - Path 3
        { background: '#F44336', border: '#D32F2F' },  // Red - Path 4
        { background: '#00BCD4', border: '#0097A7' },  // Cyan - Path 5
        { background: '#E91E63', border: '#C2185B' },  // Pink - Path 6
        { background: '#FFC107', border: '#FFA000' },  // Amber - Path 7
      ];
      return pathColors[node.path_idx % pathColors.length];
    }
    
    // Fallback to path_type for older data
    if (node.path_type) {
      const pathColors = {
        'main': { background: '#4CAF50', border: '#388E3C' },
        'secondary': { background: '#2196F3', border: '#1976D2' },
        'branch': { background: '#FF9800', border: '#F57C00' }
      };
      return pathColors[node.path_type] || pathColors.branch;
    }
    
    // Default depth-based colors
    const colors = [
      '#f7931a', // Orange (closest)
      '#ff6b35',
      '#f08a5d',
      '#b83b5e',
      '#6a2c70',
      '#08d9d6',
      '#252a34',
      '#ff2e63',
      '#eaeaea',
      '#fe346e',
    ];
    const color = colors[Math.min(node.depth || 0, colors.length - 1)];
    return {
      background: color,
      border: color
    };
  };

  const getEdgeColor = (edge) => {
    // Color by path index
    if (edge.path_idx !== undefined) {
      const pathColors = [
        { color: '#4CAF50', highlight: '#66BB6A' },  // Green
        { color: '#2196F3', highlight: '#64B5F6' },  // Blue
        { color: '#9C27B0', highlight: '#BA68C8' },  // Purple
        { color: '#FF9800', highlight: '#FFB74D' },  // Orange
        { color: '#F44336', highlight: '#E57373' },  // Red
        { color: '#00BCD4', highlight: '#4DD0E1' },  // Cyan
        { color: '#E91E63', highlight: '#F06292' },  // Pink
        { color: '#FFC107', highlight: '#FFD54F' },  // Amber
      ];
      return pathColors[edge.path_idx % pathColors.length];
    }
    
    // Fallback to path_type
    if (edge.path_type === 'main') {
      return { color: '#4CAF50', highlight: '#66BB6A' };
    } else if (edge.path_type === 'secondary') {
      return { color: '#2196F3', highlight: '#64B5F6' };
    } else if (edge.path_type === 'branch') {
      return { color: '#FF9800', highlight: '#FFB74D' };
    }
    return { color: '#f7931a', highlight: '#ff6b35' };
  };

  // Wait for container to be fully mounted
  useEffect(() => {
    if (containerRef.current) {
      // Use RAF to ensure DOM is settled
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setContainerReady(true);
        });
      });
    }
  }, []);

  useEffect(() => {
    if (!data || !containerRef.current || !containerReady) return;

    console.log('TransactionGraph: Rendering graph with data:', {
      nodes: data.nodes?.length,
      edges: data.edges?.length,
      containerReady,
      containerExists: !!containerRef.current
    });

    // Wrap in try-catch to handle any ResizeObserver errors
    try {
      // Prepare nodes
      const nodes = new DataSet(
        data.nodes.map(node => {
          // Build entity info for tooltip
          let entityInfo = '';
          let isTainted = false;
          let taintVerified = false;
          let isKnownEntity = false;
          
          // Check if node is tainted
          if (node.tainted_info) {
            isTainted = true;
            taintVerified = node.tainted_info.verified || false;
            const taint = node.tainted_info;
            const taintEmoji = taintVerified ? '🚨' : '⚠️';
            entityInfo += `\n\n${taintEmoji} TAINTED ADDRESS`;
            if (!taintVerified) {
              entityInfo += ' (Auto-detected)';
            }
            entityInfo += `\nRisk: ${taint.category}\nSeverity: ${taint.severity}/10`;
            if (taint.description) {
              entityInfo += `\n${taint.description}`;
            }
            entityInfo += `\nSource: ${taint.source}`;
            entityInfo += '\n\n⚠️ WARNING: This address is flagged for fraudulent activity!';
          }
          
          // Check if node is known entity
          if (node.known_entity) {
            isKnownEntity = true;
            const entity = node.known_entity;
            const riskEmoji = entity.risk_level === 'high_risk' ? '🚨' :
                            entity.risk_level === 'suspicious' ? '⚠️' : '✅';
            entityInfo += `\n\n${riskEmoji} Known Entity: ${entity.label}\nAddress: ${entity.address}\nType: ${entity.entity_type}\nRisk: ${entity.risk_level}\nSource: ${entity.source || 'Manual'}`;
          }
          
          if (entityInfo) {
            entityInfo += '\n\n👆 Click for details';
          }
          
          return {
            id: String(node.id),  // Ensure ID is string
            label: node.label,
            title: node.type === 'unavailable' 
              ? 'Data unavailable (transaction too old for API)'
              : `${node.is_coinbase ? '⛏️ COINBASE (Mining Reward)\n' : ''}${node.path_type ? `Path: ${node.path_type}\n` : ''}Block: ${node.block_height || 'Pending'}\nValue: ${node.total_value ? node.total_value.toFixed(8) : 'N/A'} BTC\nDepth: ${node.depth}${entityInfo}`,
            color: node.type === 'unavailable' ? '#666666' : getNodeColor({...node, is_tainted: isTainted, taint_verified: taintVerified, is_known_entity: isKnownEntity}),
            shape: node.is_coinbase ? 'star' : (node.type === 'unavailable' ? 'triangleDown' : 'box'),
            font: { 
              color: node.type === 'unavailable' ? '#ffaa00' : (node.is_coinbase ? '#000000' : '#ffffff'),
              size: node.is_coinbase ? 16 : (node.known_entity || isTainted ? 15 : 14),
              bold: node.is_coinbase || node.path_type === 'main' || node.known_entity || isTainted
            },
            size: node.is_coinbase ? 35 : (isTainted ? 35 : (node.known_entity ? 30 : 25 + (10 - (node.depth || 0)) * 3)),
            borderWidth: node.is_coinbase ? 4 : (isTainted ? 4 : (node.known_entity ? 4 : (node.path_type === 'main' ? 3 : 2))),
            shapeProperties: {
              borderDashes: (node.known_entity?.risk_level === 'high_risk' || (isTainted && !taintVerified)) ? [5, 5] : false
            }
          };
        })
      );

      // Prepare edges
      const edges = new DataSet(
        data.edges.map(edge => ({
          from: String(edge.from),  // Ensure from is string
          to: String(edge.to),      // Ensure to is string
          label: edge.label || '',
          arrows: 'to',
          color: getEdgeColor(edge),
          font: { color: '#ffffff', size: 10, strokeWidth: 0 },
          smooth: { type: 'cubicBezier' },
          width: edge.path_type === 'main' ? 3 : 2
        }))
      );

      const graphData = { nodes, edges };

      const options = {
        layout: {
          hierarchical: {
            direction: 'UD',
            sortMethod: 'directed',
            levelSeparation: 150,
            nodeSpacing: 200,
            shakeTowards: 'roots'
          },
        },
        physics: {
          enabled: false,
        },
        interaction: {
          hover: true,
          tooltipDelay: 100,
          zoomView: true,
          dragView: true,
        },
        nodes: {
          borderWidth: 2,
          borderWidthSelected: 3,
        },
        edges: {
          width: 2,
          selectionWidth: 3,
        },
        autoResize: true,
        height: '600px',
        width: '100%'
      };

      // Create network inside RAF to sync with browser paint
      requestAnimationFrame(() => {
        if (!containerRef.current) {
          console.error('Container ref lost during RAF');
          return;
        }
        
        console.log('Creating network with nodes:', nodes.length, 'edges:', edges.length);
        networkRef.current = new Network(containerRef.current, graphData, options);
        console.log('Network created successfully');
        
        // Fit the network to view after a short delay
        setTimeout(() => {
          if (networkRef.current) {
            networkRef.current.fit({
              animation: {
                duration: 500,
                easingFunction: 'easeInOutQuad'
              }
            });
            console.log('Network fitted to view');
          }
        }, 100);

        // Add click event
        networkRef.current.on('click', (params) => {
          if (params.nodes.length > 0) {
            const nodeId = params.nodes[0];
            onNodeClick(nodeId);
          }
        });
      });
      
    } catch (error) {
      // Suppress ResizeObserver errors
      if (!error.message?.includes('ResizeObserver')) {
        console.error('Network graph error:', error);
      }
    }

    // Cleanup
    return () => {
      if (networkRef.current) {
        try {
          networkRef.current.destroy();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, [data, onNodeClick, containerReady]);

  if (!data) {
    return (
      <div className="graph-placeholder">
        <p>🔍 Suche nach einer Transaktion oder Adresse, um den Graph zu sehen</p>
      </div>
    );
  }

  if (!data.nodes || data.nodes.length === 0) {
    return (
      <div className="graph-placeholder">
        <p>⚠️ Keine Transaktionsdaten gefunden</p>
        <small>Möglicherweise ist die Transaktion zu alt oder die API antwortet nicht</small>
      </div>
    );
  }

  // Check if we reached a coinbase transaction
  const hasCoinbase = data.nodes.some(node => node.is_coinbase);
  const maxDepth = Math.max(...data.nodes.map(n => n.depth));

  return (
    <div className="transaction-graph">
      <div className="graph-header">
        <h2>📊 Transaktions-Graph</h2>
        <p>
          {data.nodes.length} Transaktionen gefunden 
          {hasCoinbase && <span style={{color: '#FFD700', marginLeft: '10px'}}>⛏️ Mining-Block erreicht!</span>}
          {!hasCoinbase && <span style={{color: '#ff6b35', marginLeft: '10px'}}>⚠️ Noch nicht am Ursprung (Max. Tiefe: {maxDepth})</span>}
        </p>
        <div className="graph-legend">
          <div className="legend-item">
            <div className="legend-icon coinbase"></div>
            <span>⛏️ Mining (Coinbase)</span>
          </div>
          <div className="legend-item">
            <div className="legend-icon transaction"></div>
            <span>📝 Transaktion</span>
          </div>
          <div className="legend-item">
            <span>→ Fluss: Von alt zu neu (oben nach unten)</span>
          </div>
        </div>
      </div>
      <div ref={containerRef} className="graph-canvas" />
    </div>
  );
}

export default TransactionGraph;
